/**
 * plume — engine plumes on every hull in the sector (report §9 R5; the fire agent's shared
 * `lib/vfx/plume_material.ts` did not exist when this landed — this is the space-local plume).
 *
 * Replaces the two fixed 1.4 m glow dots (a 26 m Sovereign and a 4.5 km dreadnought wore the same): each hull
 * gets one InstancedMesh CHILD with a nozzle per instance, seated on its stern face from its own local box
 * (`nozzleSeats` — capitals 8, frigates 4, corvettes 3, fighters 2, the same rule `vehicle_fx.ts` applies in
 * atmosphere). The shared cone (nozzle at z = 0, tail at z = 1, +Z = exhaust, the hull's stern) is stretched per
 * instance by the INSTANCE MATRIX (length from throttle and boost — no positionNode, so the instancing /
 * positionPrevious laws never come up), and ONE vec4 per instance drives the look: (throttle, boost, heat, seed).
 *
 * The emission (additive, `markMrtOverlay`, HDR so it blooms): a view-facing core (the soft-volume trick for an
 * additive cone), the axial temperature ramp — white-blue core at the nozzle → the civ hue → a dim tail
 * (space_look.plumeRamp) — scrolling turbulence, and MACH DIAMONDS one nozzle diameter apart only above 60 %
 * throttle (space_look.machDiamond — the CPU mirror), core emission 4 → 12 (plumeEmission).
 * Materials are one per tint (a handful: gold, ice, pirate, the player's blue, neutral), shared by every hull of
 * that tint; the cone geometry's buffers are shared; each hull owns only its instance matrix + vec4 buffers.
 */
import * as THREE from 'three/webgpu';
import {
  Fn, uniform, vec3, float, attribute, positionGeometry, normalView, positionViewDirection, abs, dot, pow, exp, cos,
  smoothstep, clamp, max, mix, time, atan, mx_noise_float,
} from 'three/tsl';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { nozzleCount, nozzleSeats, plumeLengthM, plumeEmission, sternExtent, STERN_SLICE } from './space_look.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

export const plumeState = { hulls: 0, nozzles: 0, materials: 0 };

let baseGeo: THREE.BufferGeometry | null = null;
/** The shared cone: radius 1 at the nozzle (z 0) widening to 1.35 at the tail (z 1) — an under-expanded vacuum plume. */
function coneGeometry(): THREE.BufferGeometry {
  if (baseGeo) return baseGeo;
  const g = new THREE.CylinderGeometry(1.35, 1, 1, 14, 10, true);
  g.rotateX(Math.PI / 2);           // the cylinder's +Y axis → +Z
  g.translate(0, 0, 0.5);           // nozzle at z = 0, tail at z = 1
  g.deleteAttribute('uv');          // the shader MEASURES positionGeometry; one buffer fewer
  g.userData.shared = true;
  baseGeo = g;
  return g;
}

const mats = new Map<number, THREE.Material>();
/** One additive plume material per tint (shared by every hull of that tint). */
function plumeMaterial(tint: number): THREE.Material {
  const have = mats.get(tint);
  if (have) return have;
  const civ = new THREE.Color(tint);
  const uCiv = uniform(new THREE.Color(civ.r, civ.g, civ.b));
  const mat = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
  const a = attribute('aPlume', 'vec4');               // (throttle, boost, heat, seed)
  const color = Fn(() => {
    const p = positionGeometry;                          // MEASURE the raw cone (z01 along the axis)
    const z = clamp(p.z, 0, 1);
    const throttle = a.x, boost = a.y, heat = a.z, seed = a.w;
    // The view-facing core: an additive cone reads as a soft volume when its silhouette fades.
    const facing = abs(dot(normalView, positionViewDirection));
    const core = pow(facing, float(2.4)); // a soft volume: the silhouette fades, never a hard tube edge
    // The axial ramp (plumeRamp): white-blue core → the civ hue → a dim violet tail.
    const wCore = float(1).sub(smoothstep(0.0, 0.25, z));
    const wTail = smoothstep(0.45, 1.0, z);
    const wCiv = max(float(0), float(1).sub(wCore).sub(wTail));
    const hot = mix(vec3(0.75, 0.88, 1.0), vec3(1.0, 1.0, 1.0), heat);
    const col = hot.mul(wCore).add(vec3(uCiv).mul(wCiv)).add(vec3(0.32, 0.22, 0.55).mul(wTail.mul(0.6)));
    // Turbulence: noise scrolled down the plume, per-hull seed.
    const ang = atan(p.y, p.x);
    const n = mx_noise_float(vec3(cos(ang).mul(2.0).add(seed), z.mul(7.0).sub(time.mul(float(9).add(throttle.mul(14)))), seed.mul(3.1)));
    const turb = float(1).add(n.mul(0.35));
    // Mach diamonds: nodes one nozzle diameter apart, only above 60 % throttle (machDiamond's mirror).
    const lenOverD = float(2).add(throttle.mul(6)).mul(mix(float(1), float(1.4), boost)).mul(0.5); // plumeLengthM / (2r)
    const zD = z.mul(lenOverD);
    const on = smoothstep(0.6, 0.8, throttle);
    const node = pow(float(0.5).add(cos(zD.mul(6.2831853)).mul(0.5)), float(8)).mul(exp(zD.div(6).negate())).mul(on);
    // Emission 4 → 12 (plumeEmission), falling along the axis; the nozzle end is the hottest.
    const emission = float(4).add(clamp(throttle, 0, 1).mul(8)).mul(mix(float(1), float(1.25), boost)).min(12);
    // Fade in off the nozzle lip and OUT to nothing at the tail (no hard end ring); half per side (DoubleSide adds both).
    const axial = pow(float(1).sub(z), float(1.6)).mul(smoothstep(0.0, 0.06, z)).mul(float(1).sub(smoothstep(0.8, 1.0, z)));
    const lum = emission.mul(0.5).mul(axial).mul(core).mul(turb).mul(float(1).add(node.mul(0.8))).mul(smoothstep(0.02, 0.12, throttle));
    return col.mul(lum);
  })();
  mat.colorNode = color;
  mat.opacityNode = float(1);
  mat.fog = false;
  mat.name = `space-plume:${tint.toString(16)}`;
  markMrtOverlay(mat);
  mat.userData.shared = true;
  mats.set(tint, mat);
  plumeState.materials = mats.size;
  return mat;
}

/** A hull's plume set: the InstancedMesh child, its nozzle seats and the per-instance vec4 buffer. */
export interface HullPlumes {
  mesh: THREE.InstancedMesh;
  seats: Array<{ x: number; y: number; z: number; r: number }>;
  data: Float32Array;
  attr: THREE.InstancedBufferAttribute;
  /** Last applied (throttle, boost) — matrices are rewritten only when they move. */
  last: [number, number];
}

const _m = new THREE.Matrix4(), _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion();

/**
 * Seat plumes on a hull: `hull` is the object whose local frame has the nose on −Z (the plume set becomes its
 * child). `lenM` picks the nozzle count. `tint` the civ hue (0xRRGGBB). Returns null for a hull with no box.
 */
export function attachPlumes(hull: THREE.Object3D, lenM: number, tint: number, seed: number): HullPlumes | null {
  const box = hullShellBox(hull);
  if (box.isEmpty()) return null;
  const n = nozzleCount(lenM);
  // The stern face is +Z (nose −Z); sit the nozzles 2 % inside it so the plume mouth meets the hull — across the
  // STERN SECTION (space_look THE STERN LAW), never the span.
  const st = sternSection(hull, box);
  const seats = nozzleSeats({ minX: st.minX, maxX: st.maxX, minY: st.minY, maxY: st.maxY, maxZ: box.max.z - (box.max.z - box.min.z) * 0.02 }, n);
  const base = coneGeometry();
  const geo = new THREE.BufferGeometry();
  geo.setIndex(base.getIndex());
  geo.setAttribute('position', base.getAttribute('position'));
  geo.setAttribute('normal', base.getAttribute('normal'));
  const data = new Float32Array(n * 4);
  for (let i = 0; i < n; i++) { data[i * 4 + 3] = (seed * 13.37 + i * 1.91) % 17; }
  const attr = new THREE.InstancedBufferAttribute(data, 4);
  attr.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('aPlume', attr);
  // SHARED BUFFERS LAW: this wrapper shares the cone's position / normal / index attributes — disposing it would
  // delete THOSE GPU buffers under every other hull's plumes (three's geometry dispose drops each attribute it
  // holds). Tagged shared: the sector sweep never disposes it; dropping the reference lets GC reclaim the wrapper
  // (the GPU shield's V9 rule — nothing ever drawn is destroyed).
  geo.userData.shared = true;
  const R = Math.max(8, (box.max.z - box.min.z) * 2.5, plumeLengthM(Math.max(...seats.map((s) => s.r)), 1, true) * 1.2);
  geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, box.max.z), R);
  const mesh = new THREE.InstancedMesh(geo, plumeMaterial(tint), n);
  mesh.name = 'space-plumes';
  mesh.frustumCulled = true;
  mesh.boundingSphere = geo.boundingSphere.clone(); // the instances move with throttle; this covers the longest burn
  mesh.renderOrder = 6;
  hull.add(mesh);
  const hp: HullPlumes = { mesh, seats, data, attr, last: [-1, -1] };
  drivePlumes(hp, 0, false, 0);
  plumeState.hulls++; plumeState.nozzles += n;
  return hp;
}

/**
 * Is this mesh the hull's own SHELL? Effect children are not: plume sets (instanced), nav-point quads (instanced
 * geometry), the shield ellipsoid and any additive / transparent glow. A box over them grows past the hull.
 */
function isHullShell(m: THREE.Mesh): boolean {
  if (!m.isMesh || !m.geometry || (m as unknown as THREE.InstancedMesh).isInstancedMesh) return false;
  if ((m.geometry as THREE.InstancedBufferGeometry).isInstancedBufferGeometry) return false;
  const mat = m.material as THREE.Material | THREE.Material[] | undefined;
  const mats = Array.isArray(mat) ? mat : mat ? [mat] : [];
  return mats.some((x) => !x.transparent && x.blending !== THREE.AdditiveBlending);
}

/**
 * The hull's SHELL box in its own frame (nose −Z): `vehicle_fx.localHullBox` minus the effect children
 * (`isHullShell`). The shield, the vents, the scorch decals and the plumes all measure the hull through it — each
 * of them is itself a child of the hull, and the order they attach in must never change the others' box.
 */
export function hullShellBox(hull: THREE.Object3D): THREE.Box3 {
  hull.updateMatrixWorld(true);
  const inv = _inv.copy(hull.matrixWorld).invert();
  const out = new THREE.Box3();
  hull.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!isHullShell(m)) return;
    if (!m.geometry.boundingBox) m.geometry.computeBoundingBox();
    if (!m.geometry.boundingBox) return;
    out.union(_bb.copy(m.geometry.boundingBox).applyMatrix4(_rel.multiplyMatrices(inv, m.matrixWorld)));
  });
  if (out.isEmpty()) out.set(new THREE.Vector3(-0.7, 0, -1.3), new THREE.Vector3(0.7, 0.8, 1.3));
  return out;
}

/**
 * The stern section's percentile extent (space_look.sternExtent over the rearmost STERN_SLICE of the shell's
 * vertices). Cached per master as FRACTIONS of the shell box — clones share their geometry and differ only by a
 * uniform scale, so the fractions carry over. Falls back to the whole box when the slice is too thin to measure.
 */
const sternCache = new WeakMap<object, { x0: number; x1: number; y0: number; y1: number }>();
function sternSection(hull: THREE.Object3D, box: THREE.Box3): { minX: number; maxX: number; minY: number; maxY: number } {
  const w = Math.max(1e-6, box.max.x - box.min.x), h = Math.max(1e-6, box.max.y - box.min.y), len = box.max.z - box.min.z;
  const ref: { key: object | null } = { key: null };
  hull.traverse((o) => { if (!ref.key && isHullShell(o as THREE.Mesh)) ref.key = (o as THREE.Mesh).geometry; });
  const key = ref.key;
  const fromFrac = (f: { x0: number; x1: number; y0: number; y1: number }) => ({ minX: box.min.x + f.x0 * w, maxX: box.min.x + f.x1 * w, minY: box.min.y + f.y0 * h, maxY: box.min.y + f.y1 * h });
  const hit = key ? sternCache.get(key) : undefined;
  if (hit) return fromFrac(hit);
  const inv = _inv.copy(hull.matrixWorld).invert();
  const z0 = box.max.z - len * STERN_SLICE;
  const xs: number[] = [], ys: number[] = [];
  hull.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!isHullShell(m)) return;
    const pos = m.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!pos) return;
    _rel.multiplyMatrices(inv, m.matrixWorld);
    const stride = Math.max(1, Math.floor(pos.count / 6000));
    for (let i = 0; i < pos.count; i += stride) {
      _v.fromBufferAttribute(pos, i).applyMatrix4(_rel);
      if (_v.z >= z0) { xs.push(_v.x); ys.push(_v.y); }
    }
  });
  const e = sternExtent(xs, ys);
  const frac = e
    ? { x0: (e.minX - box.min.x) / w, x1: (e.maxX - box.min.x) / w, y0: (e.minY - box.min.y) / h, y1: (e.maxY - box.min.y) / h }
    : { x0: 0, x1: 1, y0: 0, y1: 1 };
  if (key) sternCache.set(key, frac);
  return fromFrac(frac);
}
const _inv = new THREE.Matrix4(), _rel = new THREE.Matrix4(), _bb = new THREE.Box3(), _v = new THREE.Vector3();

/** Per frame: throttle 0..1, boost, heat 0..1. Rewrites the instance matrices only when the length changes. */
export function drivePlumes(hp: HullPlumes, throttle: number, boost: boolean, heat: number): void {
  const t = Math.max(0, Math.min(1, throttle)), b = boost ? 1 : 0;
  const d = hp.data;
  for (let i = 0; i < hp.seats.length; i++) { d[i * 4] = t; d[i * 4 + 1] = b; d[i * 4 + 2] = heat; }
  hp.attr.needsUpdate = true;
  if (Math.abs(hp.last[0] - t) < 0.02 && hp.last[1] === b) return;
  hp.last[0] = t; hp.last[1] = b;
  for (let i = 0; i < hp.seats.length; i++) {
    const s = hp.seats[i];
    const len = Math.max(0.05, plumeLengthM(s.r, t, boost));
    _p.set(s.x, s.y, s.z); _q.identity(); _s.set(s.r, s.r, len);
    hp.mesh.setMatrixAt(i, _m.compose(_p, _q, _s));
  }
  hp.mesh.instanceMatrix.needsUpdate = true;
  hp.mesh.visible = t > 0.01;
}

/** Detach a hull's plume set (nothing is disposed — the SHARED BUFFERS LAW above; GC reclaims the wrapper). */
export function detachPlumes(hp: HullPlumes): void {
  hp.mesh.parent?.remove(hp.mesh);
  plumeState.hulls = Math.max(0, plumeState.hulls - 1);
  plumeState.nozzles = Math.max(0, plumeState.nozzles - hp.seats.length);
}

/** The emission the plume core will carry at a throttle (the look telemetry mirrors the shader). */
export function plumeCoreEmission(throttle: number, boost: boolean): number { return plumeEmission(throttle, boost); }
